import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import { getApiBase } from "../api";

// ============================================================================
// POD CAPTURE — Mobile-Optimized Dark Theme
// Camera capture, touch signature, step progress, blockchain hashing
// ============================================================================

// ── Photo Capture Component ──
function PhotoCapture({ photos, setPhotos, maxPhotos = 10 }) {
  const fileRef = useRef(null);

  function handleCapture(e) {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (photos.length >= maxPhotos) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotos(prev => [...prev, {
          id: Date.now() + "-" + Math.random().toString(36).slice(2, 6),
          data: ev.target.result, name: file.name, size: file.size,
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  }

  return (
    <div>
      <button onClick={() => fileRef.current?.click()} style={{
        width: "100%", padding: "18px", background: "#3b82f6", color: "white",
        border: "none", borderRadius: 14, fontWeight: 700, fontSize: 16,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        boxShadow: "0 4px 16px rgba(59,130,246,0.3)",
        WebkitTapHighlightColor: "transparent",
        marginBottom: 12,
      }}>
        📷 Take Photo
      </button>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple
        onChange={handleCapture} style={{ display: "none" }} />

      {photos.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: 8 }}>
          {photos.map(p => (
            <div key={p.id} style={{ position: "relative", borderRadius: 10, overflow: "hidden", border: "2px solid rgba(255,255,255,0.1)" }}>
              <img src={p.data} alt="POD" style={{ width: "100%", height: 90, objectFit: "cover", display: "block" }} />
              <button onClick={() => setPhotos(prev => prev.filter(x => x.id !== p.id))} style={{
                position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.7)", color: "white",
                border: "none", borderRadius: "50%", width: 26, height: 26, cursor: "pointer",
                fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
                WebkitTapHighlightColor: "transparent",
              }}>×</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
        {photos.length}/{maxPhotos} photos
      </div>
    </div>
  );
}

// ── Signature Pad ──
function SignaturePad({ onSignature }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: x * (canvas.width / rect.width), y: y * (canvas.height / rect.height) };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath(); ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#e2e8f0"; ctx.lineWidth = 3;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.stroke();
    setHasContent(true);
  }, [isDrawing, getPos]);

  const stopDraw = useCallback(() => {
    setIsDrawing(false);
    if (hasContent && canvasRef.current) onSignature(canvasRef.current.toDataURL("image/png"));
  }, [hasContent, onSignature]);

  function clear() {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasContent(false); onSignature(null);
  }

  return (
    <div>
      <div style={{
        position: "relative", border: "2px solid rgba(255,255,255,0.12)",
        borderRadius: 14, background: "#0f172a", overflow: "hidden",
      }}>
        <canvas ref={canvasRef} width={800} height={400}
          style={{ width: "100%", height: 200, cursor: "crosshair", display: "block", touchAction: "none" }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
        />
        {!hasContent && (
          <div style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center",
            justifyContent: "center", pointerEvents: "none", color: "#334155", fontSize: 16,
          }}>
            ✍️ Receiver signs here
          </div>
        )}
        {/* Signature line */}
        <div style={{ position: "absolute", bottom: 24, left: 16, right: 16, borderBottom: "1px solid rgba(255,255,255,0.1)" }} />
        <div style={{ position: "absolute", bottom: 8, left: 16, fontSize: 10, color: "#475569" }}>
          Signature of Receiving Agent
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={clear} style={{
          padding: "8px 18px", background: "rgba(255,255,255,0.06)", border: "none",
          borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#94a3b8",
          WebkitTapHighlightColor: "transparent",
        }}>Clear</button>
      </div>
    </div>
  );
}

// ── Step Indicator ──
function StepIndicator({ step, total }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {Array.from({ length: total }, (_, i) => (
        <div key={i} style={{
          flex: 1, height: 4, borderRadius: 2,
          background: i < step ? "#22c55e" : i === step ? "#3b82f6" : "rgba(255,255,255,0.08)",
          transition: "background 0.3s",
        }} />
      ))}
    </div>
  );
}

// ── Main POD Page ──
export default function CarrierPODCapture() {
  const { id: loadId } = useParams();
  const navigate = useNavigate();
  const { carrierEmail } = useAuth();
  const apiBase = useMemo(() => getApiBase(), []);
  const [load, setLoad] = useState(null);
  const [existingPod, setExistingPod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [photos, setPhotos] = useState([]);
  const [receiverSig, setReceiverSig] = useState(null);
  const [receiverName, setReceiverName] = useState("");
  const [receiverTitle, setReceiverTitle] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");

  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    "x-carrier-email": carrierEmail || "tim@symplecode.com",
  }), [carrierEmail]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [loadRes, podRes] = await Promise.all([
          fetch(`${apiBase}/carrier/loads/${loadId}`, { headers }),
          fetch(`${apiBase}/carrier/loads/${loadId}/pod`, { headers }),
        ]);
        const loadData = await loadRes.json();
        setLoad(loadData);
        const podData = await podRes.json();
        if (podData.hasPod) setExistingPod(podData);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [loadId, apiBase, headers]);

  async function submitPOD() {
    if (!receiverName.trim()) return alert("Enter the receiver's name");
    if (!receiverSig) return alert("Receiver must sign");
    if (photos.length === 0) return alert("Take at least one delivery photo");

    setSubmitting(true);
    try {
      const res = await fetch(`${apiBase}/carrier/loads/${loadId}/pod`, {
        method: "POST", headers,
        body: JSON.stringify({
          photos: photos.map(p => p.data),
          receiverSignature: receiverSig,
          receiverName, receiverTitle, deliveryNotes,
        }),
      });
      const data = await res.json();
      if (data.success) setSuccess(data);
      else alert("Error: " + (data.detail || "Unknown error"));
    } catch (e) { alert("Error: " + e.message); }
    setSubmitting(false);
  }

  // Current step for progress indicator
  const currentStep = photos.length > 0 ? (receiverName ? (receiverSig ? 3 : 2) : 1) : 0;

  if (loading) return (
    <div style={{ textAlign: "center", padding: "80px 20px", color: "#64748b" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>Loading...
    </div>
  );

  // ── Success Screen ──
  if (success) return (
    <div style={{ padding: 16, paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}>
      <div style={{
        background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)",
        borderRadius: 20, padding: "32px 20px", textAlign: "center",
      }}>
        <div style={{ fontSize: 64, marginBottom: 12 }}>✅</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#22c55e", marginBottom: 6 }}>POD Captured!</div>
        <div style={{ fontSize: 14, color: "#86efac", marginBottom: 24 }}>{success.message}</div>

        <div style={{
          background: "rgba(0,0,0,0.2)", borderRadius: 14, padding: 16,
          textAlign: "left", marginBottom: 24,
        }}>
          <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 6 }}>
            <strong style={{ color: "#e2e8f0" }}>Document:</strong> {success.documentNumber}
          </div>
          <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 6 }}>
            <strong style={{ color: "#e2e8f0" }}>Photos:</strong> {success.photoCount}
          </div>
          <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 6 }}>
            <strong style={{ color: "#e2e8f0" }}>Signed by:</strong> {receiverName}
          </div>
          <div style={{ fontSize: 10, fontFamily: "monospace", color: "#475569", marginTop: 10 }}>
            🔗 {success.contentHash?.slice(0, 28)}...
          </div>
          <div style={{ fontSize: 10, fontFamily: "monospace", color: "#475569" }}>
            ⛓️ {success.blockHash?.slice(0, 28)}...
          </div>
        </div>

        <button onClick={() => navigate("/app/carrier/assigned")} style={{
          width: "100%", padding: "16px", background: "#22c55e", color: "white",
          border: "none", borderRadius: 14, fontWeight: 700, fontSize: 16, cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
        }}>
          ← Back to My Loads
        </button>
      </div>
    </div>
  );

  // ── Existing POD View ──
  if (existingPod) return (
    <div style={{ padding: 14, paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}>
      <div style={{ background: "#1e293b", borderRadius: 20, border: "1px solid rgba(255,255,255,0.08)", overflow: "hidden" }}>
        <div style={{ background: "linear-gradient(135deg, #7c3aed, #6d28d9)", padding: "16px 18px" }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "white" }}>✅ POD Already Captured</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>{existingPod.documentNumber}</div>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 14, color: "#e2e8f0", marginBottom: 12 }}>
            <strong>Received by:</strong> {existingPod.receiverName}
            {existingPod.receiverTitle && <span style={{ color: "#64748b" }}> — {existingPod.receiverTitle}</span>}
          </div>
          {existingPod.deliveryNotes && (
            <div style={{ padding: 12, background: "rgba(0,0,0,0.2)", borderRadius: 10, marginBottom: 12, fontSize: 13, color: "#94a3b8" }}>
              {existingPod.deliveryNotes}
            </div>
          )}
          {existingPod.receiverSignatureUrl && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>Receiver Signature:</div>
              <div style={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: 8, background: "#0f172a" }}>
                <img src={existingPod.receiverSignatureUrl} alt="Signature" style={{ width: "100%", height: 80, objectFit: "contain" }} />
              </div>
            </div>
          )}
          {existingPod.photos?.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>Photos ({existingPod.photoCount}):</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 8 }}>
                {existingPod.photos.map((p, i) => (
                  <img key={i} src={p.url} alt={`POD ${i+1}`} style={{
                    width: "100%", height: 100, objectFit: "cover", borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.08)",
                  }} />
                ))}
              </div>
            </div>
          )}
          <div style={{ marginTop: 14, fontSize: 10, fontFamily: "monospace", color: "#475569" }}>
            🔗 {existingPod.contentHash?.slice(0, 32)}...
          </div>
          <button onClick={() => navigate(-1)} style={{
            width: "100%", marginTop: 16, padding: "14px", background: "rgba(255,255,255,0.06)",
            border: "none", borderRadius: 12, fontWeight: 600, cursor: "pointer", color: "#94a3b8",
            WebkitTapHighlightColor: "transparent",
          }}>← Go Back</button>
        </div>
      </div>
    </div>
  );

  // ── POD Capture Form ──
  return (
    <div style={{ padding: "0 0 20px", paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}>
      {/* Header */}
      <div style={{
        padding: "14px 14px 16px",
        background: "linear-gradient(135deg, #581c87, #7c3aed)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <button onClick={() => navigate(-1)} style={{
            width: 40, height: 40, borderRadius: 12, border: "1px solid rgba(255,255,255,0.2)",
            background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer",
            fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center",
            WebkitTapHighlightColor: "transparent",
          }}>←</button>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "white" }}>📦 Proof of Delivery</div>
            {load && (
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                {load.loadNumber || load.referenceNumber} · {typeof load.destCity === "string" ? load.destCity : load.destAddress?.city || "Delivery"}, {typeof load.destState === "string" ? load.destState : load.destAddress?.state || ""}
              </div>
            )}
          </div>
        </div>
        <StepIndicator step={currentStep} total={3} />
      </div>

      <div style={{ padding: "12px 14px" }}>
        {/* Step 1: Photos */}
        <div style={{
          background: "#1e293b", borderRadius: 16, padding: 16,
          border: "1px solid rgba(255,255,255,0.06)", marginBottom: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{
              width: 24, height: 24, borderRadius: 24, fontSize: 12, fontWeight: 800,
              background: photos.length > 0 ? "#22c55e" : "#3b82f6", color: "white",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{photos.length > 0 ? "✓" : "1"}</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9" }}>Delivery Photos</span>
          </div>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12, paddingLeft: 32 }}>
            Photograph delivered goods, location, and any damage
          </div>
          <PhotoCapture photos={photos} setPhotos={setPhotos} />
        </div>

        {/* Step 2: Receiver Info */}
        <div style={{
          background: "#1e293b", borderRadius: 16, padding: 16,
          border: "1px solid rgba(255,255,255,0.06)", marginBottom: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{
              width: 24, height: 24, borderRadius: 24, fontSize: 12, fontWeight: 800,
              background: receiverName ? "#22c55e" : "#3b82f6", color: "white",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{receiverName ? "✓" : "2"}</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9" }}>Receiver Info</span>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13, color: "#94a3b8" }}>Name *</label>
            <input value={receiverName} onChange={e => setReceiverName(e.target.value)}
              placeholder="Person accepting delivery"
              style={{
                width: "100%", padding: "14px", borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.3)",
                color: "#f1f5f9", fontSize: 16, boxSizing: "border-box", outline: "none",
              }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13, color: "#94a3b8" }}>Title</label>
            <input value={receiverTitle} onChange={e => setReceiverTitle(e.target.value)}
              placeholder="e.g., Warehouse Manager"
              style={{
                width: "100%", padding: "14px", borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.3)",
                color: "#f1f5f9", fontSize: 16, boxSizing: "border-box", outline: "none",
              }}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13, color: "#94a3b8" }}>Notes</label>
            <textarea value={deliveryNotes} onChange={e => setDeliveryNotes(e.target.value)}
              placeholder="Condition, damages, count verified..."
              rows={2}
              style={{
                width: "100%", padding: "14px", borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.3)",
                color: "#f1f5f9", fontSize: 16, boxSizing: "border-box", outline: "none",
                resize: "none", fontFamily: "inherit",
              }}
            />
          </div>
        </div>

        {/* Step 3: Signature */}
        <div style={{
          background: "#1e293b", borderRadius: 16, padding: 16,
          border: "1px solid rgba(255,255,255,0.06)", marginBottom: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{
              width: 24, height: 24, borderRadius: 24, fontSize: 12, fontWeight: 800,
              background: receiverSig ? "#22c55e" : "#3b82f6", color: "white",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{receiverSig ? "✓" : "3"}</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9" }}>Receiver Signature</span>
          </div>
          <SignaturePad onSignature={setReceiverSig} />
        </div>

        {/* Blockchain notice */}
        <div style={{
          padding: "10px 14px", borderRadius: 12, marginBottom: 14,
          background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.12)",
          display: "flex", gap: 8, alignItems: "flex-start",
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>🔗</span>
          <div style={{ fontSize: 12, color: "#93c5fd", lineHeight: 1.4 }}>
            This POD will be blockchain-recorded with SHA-256 hashes of all photos and the signature — creating a tamper-proof delivery record.
          </div>
        </div>

        {/* Submit */}
        <button onClick={submitPOD}
          disabled={submitting || !receiverName || !receiverSig || photos.length === 0}
          style={{
            width: "100%", padding: "18px", borderRadius: 14, border: "none",
            background: (submitting || !receiverName || !receiverSig || photos.length === 0)
              ? "#334155" : "linear-gradient(135deg, #22c55e, #16a34a)",
            color: (submitting || !receiverName || !receiverSig || photos.length === 0)
              ? "#64748b" : "white",
            fontWeight: 900, fontSize: 17, cursor: "pointer",
            boxShadow: (receiverName && receiverSig && photos.length > 0 && !submitting)
              ? "0 6px 20px rgba(34,197,94,0.35)" : "none",
            WebkitTapHighlightColor: "transparent",
            marginBottom: 12,
          }}
        >
          {submitting ? "⏳ Uploading POD..." : "✅ Submit Proof of Delivery"}
        </button>

        {/* Progress summary */}
        <div style={{ display: "flex", gap: 12, justifyContent: "center", fontSize: 12, color: "#475569" }}>
          <span style={{ color: photos.length > 0 ? "#22c55e" : "#334155" }}>📷 {photos.length}</span>
          <span style={{ color: receiverName ? "#22c55e" : "#334155" }}>👤 {receiverName || "—"}</span>
          <span style={{ color: receiverSig ? "#22c55e" : "#334155" }}>✍️ {receiverSig ? "Signed" : "—"}</span>
        </div>
      </div>
    </div>
  );
}
